import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { authApi } from "@/features/auth/auth.api";
import { AuthShell } from "@/features/auth/AuthShell";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FormField } from "@/components/ui/FormField";
import { getErrorMessage } from "@/api/client";

const schema = z
  .object({
    newPassword: z.string().min(8, "Password must be at least 8 characters").max(200),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";

  const [status, setStatus] = useState<"idle" | "success">("idle");
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    setServerError(null);
    try {
      await authApi.resetPassword(token, values.newPassword);
      setStatus("success");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setServerError(getErrorMessage(err, "This reset link is invalid or has expired."));
    }
  }

  // No token in the URL — the link was malformed or truncated.
  if (!token) {
    return (
      <AuthShell
        title="Invalid reset link"
        footer={
          <Link to="/forgot-password" className="text-brand-600 hover:underline dark:text-brand-500">
            Request a new link
          </Link>
        }
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500 dark:bg-slate-700">
            <IoWarningOutline size={24} />
          </span>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            This password reset link is missing its token. Please request a new one.
          </p>
        </div>
      </AuthShell>
    );
  }

  if (status === "success") {
    return (
      <AuthShell
        title="Password updated"
        footer={
          <Link to="/login" className="text-brand-600 hover:underline dark:text-brand-500">
            Continue to sign in
          </Link>
        }
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-600 dark:bg-slate-700">
            <IoCheckmarkCircleOutline size={26} />
          </span>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Your password has been reset. Redirecting you to sign in…
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set a new password"
      subtitle="Choose a strong password for your account."
      footer={
        <Link to="/login" className="text-brand-600 hover:underline dark:text-brand-500">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormField label="New password" htmlFor="newPassword" error={errors.newPassword?.message} required>
          <Input id="newPassword" type="password" autoComplete="new-password" {...register("newPassword")} />
        </FormField>
        <FormField label="Confirm password" htmlFor="confirmPassword" error={errors.confirmPassword?.message} required>
          <Input id="confirmPassword" type="password" autoComplete="new-password" {...register("confirmPassword")} />
        </FormField>

        {serverError && <p className="text-sm text-red-500">{serverError}</p>}

        <Button type="submit" isLoading={isSubmitting} className="mt-1 w-full">
          Reset password
        </Button>
      </form>
    </AuthShell>
  );
}
